
import RouteParams from '@/entities/RouteParams';
import RouteNames from '@/entities/RouteNames';
import CountryFlag from 'vue-country-flag';
import getCountryCode from '@/utils/getCountryCode';
import {
	Contractor,
	Contractor_Rating_Aggregate,
	Manager,
	Project,
	User,
	Tag,
} from '@/generated/graphql';
import ToggleFavorite from '@/components/favorite/ToggleFavoriteRequests.vue';
import ChatIcon from '@/components/icons/ChatIconNew.vue';
import ChatIconHover from '@/components/icons/ChatIconHoverNew.vue';
import ContractorRating from '@/components/rating/ContractorRatingNew.vue';
import ResourceCards from '@/components/card/ResourceCards.vue';
import getLocationInfo from '@/helpers/locations/getLocationInfo';
import BadgeColor from '@/entities/BadgeColor';
import Contractor_PROJECTS from '@/apollo/contractors/GetContractorProjects.gql';
import Badge from '@/components/common/BadgeNew.vue';
import { emailStatusText, managerSuspendedText } from '@/data/userStatuses';
import ResourceAvatar from '@/components/card/ResourceAvatar.vue';
import ActionSection from '@/components/card/ActionSection.vue';
import EmployeeTag from '@/components/employee/EmployeeTagNew.vue';
import { Vue, Component, Prop } from 'vue-property-decorator';

interface IActionDefault {
	leftTitle: string;
	leftValue: number | string;
	rightTitle: string;
	rightValue: number | string;
}

interface IActionError {
	title: string;
	description: number | string;
	actionButton: string;
}

interface ProjectsType {
	project?: [
		{
			completed: Boolean;
		}
	];
}

interface IManagerProject {
	id: string;
	name: string;
}

interface IActionSection {
	state: boolean;
	default: IActionDefault;
	error: IActionError;
}

@Component({
	components: {
		ResourceCards,
		CountryFlag,
		Badge,
		ChatIcon,
		ResourceAvatar,
		ActionSection,
		ChatIconHover,
		ToggleFavorite,
		ContractorRating,
		EmployeeTag,
	},
	apollo: {
		projects: {
			query: Contractor_PROJECTS,
			variables() {
				return this.conditions;
			},
			update(data) {
				const totalActiveProj = data.project.filter(
					(proj) => !proj.completed && !proj.drafted
				).length;
				this.activeProjects = totalActiveProj;
				return data;
			},
		},
	},
})
export default class ManagerCard extends Vue {
	@Prop({ type: Object, required: true }) readonly contractor!: Contractor;

	BadgeColor: typeof BadgeColor = BadgeColor;
	emailStatusText: string = emailStatusText;
	isHovered: boolean = false;
	activeProjects: number = null;
	@Prop({ type: String, required: true }) readonly companyId!: string;
	@Prop({ type: Object, required: true }) readonly currentUser!: User;
	@Prop({ type: String, required: true }) readonly userUpdatedDate!: string;
	@Prop({ type: String, required: true }) readonly currentUserId!: string;
	@Prop({ type: Boolean, required: true }) readonly getRoleAdmin!: boolean;

	get conditions(): Object {
		return {
			where: {
				project_activities: {
					contractor: {
						user_id: {
							_eq: this.contractor.user.id,
						},
					},
				},
			},
		};
	}

	get appliedTag(): Tag | undefined {
		return this.contractor?.tag || undefined;
	}

	get getActionSectionValues(): IActionSection {
		if (this.pendingInvite) {
			return {
				state: true,
				default: {
					leftTitle: 'Internal profile',
					leftValue:
						'Profile has been internally added to database. Resource not invited.',
					rightTitle: '',
					rightValue: '',
				},
				error: {
					title: 'Internal profile',
					description:
						'Profile has been internally added to database. Resource not invited.',
					actionButton: 'internal',
				},
			};
		}

		// Existing logic for other conditions
		const actionSectionResult: IActionSection = {
			state: this.isEmailUnconfirmed,
			default: {
				leftTitle: 'Active Clients',
				leftValue: this.activeProjects,
				rightTitle: '',
				rightValue: '',
			},
			error: {
				title: this.emailStatusText,
				description: '',
				actionButton: 'resend',
			},
		};

		return actionSectionResult;
	}

	get contractorFullName(): string {
		return `${this.contractor.user.given_name} ${this.contractor.user.family_name}`;
	}

	get contractorInitials(): string {
		return `${this.contractor.user.given_name![0]}${
			this.contractor.user.family_name![0]
		}`;
	}

	get contractorMinRate(): string | undefined {
		const minRate = this.contractor.user.profile.min_rate || this.contractor.user.user_profile_by_managements[0]?.min_rate;

		if (!minRate) {
			return 'n/a';
		}

		return `$${minRate}`;
	}

	get contractorMaxRate(): string | undefined {
		const maxRate = this.contractor.user.profile.max_rate || this.contractor.user.user_profile_by_managements[0]?.max_rate;
		const minRate = this.contractor.user.profile.min_rate || this.contractor.user.user_profile_by_managements[0]?.min_rate;
		const rateUnit = this.contractor.user.profile.rate_unit || this.contractor.user.user_profile_by_managements[0]?.rate_unit;

		if (!maxRate) {
			return ``;
		}
		if (!maxRate && minRate) {
			return `${rateUnit}`;
		}
		return `-$${maxRate}`;
	}

	get contractorRateUnit(): string | undefined {
		const maxRate = this.contractor.user.profile.max_rate || this.contractor.user.user_profile_by_managements[0]?.max_rate;
		const minRate = this.contractor.user.profile.min_rate || this.contractor.user.user_profile_by_managements[0]?.min_rate;
		const rateUnit = this.contractor.user.profile.rate_unit || this.contractor.user.user_profile_by_managements[0]?.rate_unit;

		if (!minRate && !maxRate && !rateUnit) {
			return '';
		}

		return `/${rateUnit}`;
	}
	get skills() {
		const skills = this.contractor.user.profile.skills;
		return skills;
	}

	get managerInitials(): string {
		return `${this.contractor.user.given_name![0]}${
			this.contractor.user.family_name![0]
		}`;
	}

	get managerAvatar(): string | undefined {
		return this.contractor.user.avatar || undefined;
	}

	get pendingInvite(): boolean | undefined {
		return this.contractor?.user?.pending_invite || undefined;
	}

	message(): void {
		this.$router.push({
			name: 'inbox',
			query: {
				recipientUserId: this.contractor.user.id,
			},
		});
	}

	get isEmailUnconfirmed(): boolean {
		return !this.contractor.user.confirmed;
	}

	get isCardDisabled(): boolean {
		return this.isEmailUnconfirmed;
	}
	get country() {
		const userCountry = this.contractor.user.profile.country ? this.contractor.user.profile.country : this.contractor.user.user_profile_by_managements[0]?.country;
		return getCountryCode(userCountry);
	}

	get managerFullName(): string {
		return `${this.contractor.user.given_name} ${this.contractor.user.family_name}`;
	}

	get contractorPageRoute(): RouteParams<{ id: string }> {
		// TODO: Needs to see if it is needed
		// if (this.contractorDetailsPageRoute) {
		//   return this.contractorDetailsPageRoute;
		// }

		return {
			name: RouteNames.CONTRACTOR_DETAILS_ABOUT,
			params: {
				id: this.contractor.id,
			},
		};
	}

	get managerLocation(): string {
		return getLocationInfo(this.contractor.user.profile!);
	}

	get managerJobTitle(): string {
		// First check if user_job_title is available (using type assertion for new GraphQL field)
		const profile = this.contractor.user?.profile as any;
		if (profile?.user_job_title?.job_title) {
			return profile.user_job_title.job_title;
		}
		
		// Fallback to legacy job_title field
		if (profile?.job_title) {
			return profile.job_title;
		}
		
		// Final fallback to first experience job title
		if (this.contractor.user?.experiences?.[0]?.job_title) {
			return this.contractor.user.experiences[0].job_title;
		}
		
		return "Job title not set";
	}
}
